import { useEffect, useState } from "react";
import { usePage } from "@inertiajs/react";
import { CheckIcon, CloseIcon, WarningIcon } from "./icons";

// Toasts for the controller's flash and for validation errors. Rejected forms
// mark the offending fields inline, but those fields are easily off-screen, so
// the same messages are repeated up here where they can't be missed.
//
// Each one leads with a word saying which of the three happened — it went through, it didn't
// save, or something else stopped it — and then a sentence saying what that means for the
// records. The heading is decided here rather than sent from the controller, so a controller
// only ever writes the sentence and the three can't drift into three different vocabularies.
const HEADINGS = {
  success: "Success",
  invalid: "That didn't save",
  refused: "Couldn't do that",
};

export default function FlashMessages() {
  const { flash, errors } = usePage().props;
  const toast = pick(flash, errors);
  const key = toast ? toast.lines.join("|") : null;
  const [dismissed, setDismissed] = useState(false);

  // Keyed on the text so repeating an action re-shows it rather than staying
  // dismissed from last time.
  useEffect(() => {
    setDismissed(false);
    if (!key) return;

    const timer = setTimeout(() => setDismissed(true), toast.isError ? 8000 : 5000);
    return () => clearTimeout(timer);
  }, [key]);

  if (!toast || dismissed) return null;

  return (
    // Over the top bar rather than below it. A notice tucked under the bar is easy to finish
    // a task without ever seeing; up here it lands where the eye already is after pressing
    // Save. Click-through, so the few seconds it spends over the account chip cost nothing —
    // only its own dismiss button takes pointer events back.
    <div className="pointer-events-none fixed inset-x-4 top-3 z-50 flex justify-center sm:inset-x-6">
      <div
        role={toast.isError ? "alert" : "status"}
        aria-live={toast.isError ? "assertive" : "polite"}
        className={`flex w-full max-w-lg items-start gap-3 rounded-xl border px-4 py-3 shadow-xl ring-1 animate-pop-in ${
          toast.isError
            ? "border-red-200 bg-red-50 text-red-900 ring-red-900/5"
            : "border-emerald-200 bg-emerald-50 text-emerald-900 ring-emerald-900/5"
        }`}
      >
        <span
          className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
            toast.isError ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"
          }`}
        >
          {toast.isError ? (
            <WarningIcon className="h-4 w-4" />
          ) : (
            <CheckIcon className="h-4 w-4" />
          )}
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold">{HEADINGS[toast.kind]}</p>
          {toast.lines.length === 1 ? (
            <p className="mt-0.5 text-sm leading-snug">{toast.lines[0]}</p>
          ) : (
            <ul className="mt-1 list-inside list-disc space-y-0.5 text-sm leading-snug">
              {toast.lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </div>

        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="pointer-events-auto -mr-1 shrink-0 rounded p-0.5 opacity-60 transition hover:opacity-100"
        >
          <CloseIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// A rejected save is the most urgent thing on screen, so validation errors
// outrank a success notice left over from the previous page.
function pick(flash, errors) {
  const messages = Object.values(errors ?? {})
    .flat()
    .filter(Boolean);

  // A refusal and a rejected form are both failures but not the same kind: one means the
  // form was wrong, the other that the records won't allow what was asked. Headed apart, so
  // the reader knows whether to fix a field or go and do something else first.
  if (flash?.alert) return { isError: true, kind: "refused", lines: [flash.alert] };
  if (messages.length) return { isError: true, kind: "invalid", lines: messages };
  if (flash?.notice) return { isError: false, kind: "success", lines: [flash.notice] };

  return null;
}
